// Shared types for the per-user Preferences modal.
//
// Server owns the write path (POST /api/user/preferences) and mirrors
// this shape in server/src/routes/user.ts (sanitizePreferences). Keep
// the two in sync — adding a field here without a server-side allow
// means the field is silently stripped on save.

export type DefaultModule =
  | 'map'
  | 'research'
  | 'charts'
  | 'alerts'
  | 'expressions'
  | 'baseball_cards'
  | 'simulation'
  | 'biosurveillance'
  | 'monitor'
  | 'infrastructure'
  | 'custom_ui';

/** Pointer to a specific saved Custom UI. Scope decides which Firestore
 *  collection the id lives in. User-scope UIs are always under the viewer's
 *  own uid in Phase 1; org-scope lands in Phase 2. */
export interface CustomUiRef {
  scope: 'user' | 'organization';
  uiId: string;
}

/** Map-layer toggles the Preferences modal exposes. The keys mirror the
 *  camel-cased pieces of MapPage state (`layerVisible`, `cellEnabled`,
 *  `wifiEnabled`, …) so applying a saved preference is a direct setter
 *  loop rather than a schema translation. */
export interface MapPreferenceLayers {
  polygons?: boolean;
  poi?: boolean;
  cell?: boolean;
  wifi?: boolean;
  events?: boolean;
}

/** Starting filter state for the events (alert map) layer. Mirrors the
 *  shape of `AlertMapFilters` in useAlertMap, but only the three
 *  category-style selects users said they want pre-seeded. Empty / missing
 *  = "no filter" (same semantic as the EventsPanel's "All" / "Any"). */
export type EventsTimeline = 'day' | 'week' | 'month';

export interface MapPreferenceEvents {
  topic?: string;     // '' = all; otherwise one of ALERT_TOPICS
  severity?: string;  // '' = any, '1'..'5' = min
  urgency?: string;   // '' = any, '1'..'5' = min
  /** How far back to pre-populate the from/to date filter. The seed
   *  resolves this to concrete YYYY-MM-DD values the EventsPanel's
   *  date inputs understand. */
  timeline?: EventsTimeline;
}

export interface MapPreference {
  layers?: MapPreferenceLayers;
  /** Country code OR display name — whatever CountrySelector normalises
   *  to. Null / missing means "no auto-zoom; keep the default world
   *  view." */
  defaultCountry?: string | null;
  /** Per-category POI toggles. Only read when `layers.poi` is true. Keys
   *  must match POI_CATEGORIES ids. Missing / empty means "turn every
   *  category on" so the plain POI toggle keeps its old behaviour for
   *  users who never opened the sub-panel. */
  poiCategories?: Record<string, boolean>;
  /** Pre-seeded event filters applied once on mount when `layers.events`
   *  is true. Users can still change them live from the Events panel. */
  events?: MapPreferenceEvents;
  /** Basemap id from BASEMAPS (BasemapSelector). Null / missing keeps
   *  the app default. A saved view's basemap still wins when restored. */
  basemap?: string | null;
}

export interface ChartsPreference {
  orgId?: string | null;
  projectId?: number | null;
  formId?: number | null;
}

/** Which parts of the daily briefing the user wants to see. Keys match
 *  the section names the BriefingModal uses internally; add a key here
 *  and a matching render-gate there to introduce a new section. */
export interface BriefingSections {
  summary?: boolean;      // AI events summary
  premise?: boolean;      // Premise results (forms + changes)
  events?: boolean;       // Scraped-event feed
}

export interface BriefingPreference {
  enabled?: boolean;
  sections?: BriefingSections;
  /** Up to 3 country names / ISO codes to force into the briefing's
   *  scope. Empty / missing means "use the auto-ranked top countries"
   *  the existing /api/briefing/countries endpoint returns. */
  countries?: string[];
}

export interface UserPreferences {
  version?: number;
  completed_at?: string | null;
  defaultModule?: DefaultModule | null;
  map?: MapPreference;
  charts?: ChartsPreference;
  briefing?: BriefingPreference;
  /** When defaultModule === 'custom_ui', which saved UI to open. Null /
   *  missing falls back to the standard landing page with a one-time
   *  toast. */
  defaultCustomUi?: CustomUiRef | null;
}

/** Default preference seed. Used by the modal's "first time" state so
 *  the briefing is on by default (matches current product behaviour) and
 *  the map defaults match the current layer-visibility seed. */
export const DEFAULT_PREFERENCES: UserPreferences = {
  defaultModule: 'map',
  map: {
    layers: { polygons: true, poi: false, cell: false, wifi: false, events: false },
    defaultCountry: null,
    poiCategories: {},
    events: { topic: '', severity: '', urgency: '', timeline: 'week' },
    basemap: null,
  },
  charts: { orgId: null, projectId: null, formId: null },
  briefing: {
    enabled: true,
    sections: { summary: true, premise: true, events: true },
    countries: [],
  },
};

/** Route path each default-module value maps to. Centralised so the
 *  LoginPage, the user menu "jump to default" affordance, and future
 *  callers all stay aligned. */
export const MODULE_ROUTES: Record<DefaultModule, string> = {
  map:              '/map',
  research:         '/research',
  charts:           '/aggregations',
  alerts:           '/alerts',
  expressions:      '/expressions',
  baseball_cards:   '/baseball-cards',
  simulation:       '/simulation',
  biosurveillance:  '/alerts',    // no dedicated route — biosurveillance lives inside alerts
  monitor:          '/map',       // monitor is a sidebar panel on /map
  infrastructure:   '/map',       // infrastructure is a map layer
  custom_ui:        '/custom-ui', // resolves to defaultCustomUi.uiId at runtime
};

/** Human-readable labels for each module pill in the Preferences modal. */
export const MODULE_LABELS: Record<DefaultModule, string> = {
  map:             'Map',
  research:        'Research',
  charts:          'Charts',
  alerts:          'Alerts',
  expressions:     'Expressions',
  baseball_cards:  'Baseball Cards',
  simulation:      'Simulation',
  biosurveillance: 'Biosurveillance',
  monitor:         'Monitor',
  infrastructure:  'Infrastructure',
  custom_ui:       'Custom UI',
};

/** Which modules the user actually sees in the pill list is driven by
 *  their merged module access. Map and Charts have no module gate
 *  (available to everyone with an org); the rest require the matching
 *  flag. Keep this in lockstep with SidebarNav gating. */
export const MODULE_REQUIRES: Partial<Record<DefaultModule, string>> = {
  research:        'research',
  alerts:          'alerts',
  expressions:     'expressions',
  baseball_cards:  'baseball_cards',
  simulation:      'simulation',
  biosurveillance: 'biosurveillance',
  monitor:         'monitor',
  infrastructure:  'infrastructure',
  custom_ui:       'custom_ui',
};
